<script setup lang="ts">
import type { ValidData as DriverReportGenerateDialogFormValidData } from '@/components/dialogForms/DriverReportGenerateDialogForm.vue';
import type { DialogName } from '@/stores/useDialogStore';

import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import DriverReportSearchForm from '@/components/searchForms/DriverReportSearchForm.vue';
import DriverReportTable from '@/components/tables/DriverReportTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import useDownloadCsv from '@/composables/useDownloadCsv';
import useDriverReportView from '@/composables/views/useDriverReportView';
import { useDialogStore } from '@/stores/useDialogStore';
import { fetchPostReportsGenerateDriverReport } from '@/helpers/fetchers';
import { defCurPage, defPageSize } from '@/constants/routeQuery';

const { getCurrentQueryDate } = useCurrentDateString();
const { queryCurPage, queryPageSize, queryDate } = useCommonRouteQuery({
  queryCurPage: defCurPage,
  queryPageSize: defPageSize,
  queryDate: getCurrentQueryDate(),
});
const { isDownloadingCSV, downloadCsv } = useDownloadCsv();
const {
  search,
  initialData,
  isFetchingData,
  total,
  driverReportResults,
  resultsForCsv,
} = useDriverReportView(queryCurPage, queryPageSize, queryDate);

const dialogStore = useDialogStore();

function openDialog() {
  const dialogName: DialogName = 'driverReportGenerate';
  dialogStore.open(dialogName, {
    confirm: async (formData: DriverReportGenerateDialogFormValidData) => {
      return fetchPostReportsGenerateDriverReport(formData);
    },
  });
}
</script>

<template>
  <div :class="$style.driverReportView" data-testid="driverReportView">
    <PageContentWrapper>
      <FormWrapper :class="$style.formWrapper">
        <h2 class="g-h2">
          To list reports choose a date when a report has been created
        </h2>
        <DriverReportSearchForm
          :initial-data="initialData"
          :loading="isFetchingData"
          :class="$style.form"
          @submit="search"
        />
      </FormWrapper>
      <TablePageShadowWrapper :class="$style.tablePageShadowWrapper">
        <DriverReportTable
          :loading="isFetchingData"
          :data="driverReportResults || []"
        />
        <TablePageFooter
          v-if="driverReportResults"
          :cur-page="queryCurPage"
          :page-size="queryPageSize"
          :total="total"
        >
          <ExportAsCsvIconBtn
            :disabled="isFetchingData || !driverReportResults?.length"
            :loading="isDownloadingCSV"
            @click="downloadCsv(resultsForCsv, 'driverReport')"
          />
        </TablePageFooter>
      </TablePageShadowWrapper>
    </PageContentWrapper>
    <TableFooterBar>
      <KBtn
        size="lg"
        append-icon="mdi-send"
        :disabled="isFetchingData || !driverReportResults?.length"
        @click="openDialog"
      >
        Generate new report
      </KBtn>
    </TableFooterBar>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.driverReportView {
}
  .formWrapper {
    padding: 0.5rem 1.5rem 0.5rem 1.5rem;
    max-width: variables.$tableWrapperWidth;
    margin: 2rem 0;
  }
  .tablePageShadowWrapper {
    max-width: variables.$tableWrapperWidth;
  }
</style>
